import './Editor.module.scss'

import { Color } from '@tiptap/extension-color'
import ListItem from '@tiptap/extension-list-item'
import TextStyle from '@tiptap/extension-text-style'
import { EditorProvider, useCurrentEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import {
  Baseline,
  Bold,
  Code,
  Eraser,
  Heading1,
  Heading2,
  Heading3,
  Italic,
  List,
  ListOrdered,
  Minus,
  Pilcrow,
  Quote,
  Redo2,
  SquareCode,
  Strikethrough,
  Undo2
} from 'lucide-react'
import { type ReactNode, useEffect, useState } from 'react'

import { cn } from '@/lib/utils'

type TiptapEditor = ReturnType<typeof useCurrentEditor>['editor']

function ToolbarButton({
  onClick,
  active = false,
  disabled = false,
  title,
  children
}: {
  onClick: () => void
  active?: boolean
  disabled?: boolean
  title: string
  children: ReactNode
}) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex h-8 w-8 items-center justify-center rounded-lg border-0 bg-transparent p-0 text-ink/70 transition-colors hover:bg-ink/[0.06] hover:text-ink disabled:cursor-not-allowed disabled:opacity-30',
        active && 'bg-primary/10 text-primary hover:bg-primary/15'
      )}
    >
      {children}
    </button>
  )
}

function Divider() {
  return <span aria-hidden className="mx-1 h-5 w-px shrink-0 bg-ink/10" />
}

function ColorButton({ editor }: { editor: NonNullable<TiptapEditor> }) {
  const [color, setColor] = useState('#042125')

  useEffect(() => {
    const sync = () =>
      setColor(editor.getAttributes('textStyle').color || '#042125')
    editor.on('selectionUpdate', sync)
    editor.on('transaction', sync)
    sync()
    return () => {
      editor.off('selectionUpdate', sync)
      editor.off('transaction', sync)
    }
  }, [editor])

  return (
    <label
      title="Farba textu"
      className="relative flex h-8 w-8 cursor-pointer items-center justify-center rounded-lg text-ink/70 transition-colors hover:bg-ink/[0.06] hover:text-ink"
    >
      <Baseline className="h-4 w-4" />
      <span
        aria-hidden
        className="absolute bottom-1 left-1.5 right-1.5 h-[3px] rounded-full"
        style={{ backgroundColor: color }}
      />
      <input
        type="color"
        value={color}
        onChange={(e) => {
          setColor(e.target.value)
          editor.chain().focus().setColor(e.target.value).run()
        }}
        className="sr-only"
        aria-label="Farba textu"
      />
    </label>
  )
}

function MenuBar() {
  const { editor } = useCurrentEditor()
  if (!editor) return null

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-ink/[0.08] bg-ink/[0.02] p-2">
      <ToolbarButton
        title="Tučné"
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <Bold className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        title="Kurzíva"
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <Italic className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        title="Prečiarknuté"
        active={editor.isActive('strike')}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      >
        <Strikethrough className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        title="Kód"
        active={editor.isActive('code')}
        onClick={() => editor.chain().focus().toggleCode().run()}
      >
        <Code className="h-4 w-4" />
      </ToolbarButton>

      <Divider />

      <ToolbarButton
        title="Odsek"
        active={editor.isActive('paragraph')}
        onClick={() => editor.chain().focus().setParagraph().run()}
      >
        <Pilcrow className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        title="Nadpis 1"
        active={editor.isActive('heading', { level: 1 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
      >
        <Heading1 className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        title="Nadpis 2"
        active={editor.isActive('heading', { level: 2 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        title="Nadpis 3"
        active={editor.isActive('heading', { level: 3 })}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
      >
        <Heading3 className="h-4 w-4" />
      </ToolbarButton>

      <Divider />

      <ToolbarButton
        title="Odrážkový zoznam"
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <List className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        title="Číslovaný zoznam"
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <ListOrdered className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        title="Citácia"
        active={editor.isActive('blockquote')}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
      >
        <Quote className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        title="Blok kódu"
        active={editor.isActive('codeBlock')}
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
      >
        <SquareCode className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        title="Vodorovná čiara"
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
      >
        <Minus className="h-4 w-4" />
      </ToolbarButton>

      <Divider />

      <ColorButton editor={editor} />
      <ToolbarButton
        title="Vymazať formátovanie"
        onClick={() =>
          editor.chain().focus().unsetAllMarks().clearNodes().run()
        }
      >
        <Eraser className="h-4 w-4" />
      </ToolbarButton>

      <Divider />

      <ToolbarButton
        title="Späť"
        disabled={!editor.can().undo()}
        onClick={() => editor.chain().focus().undo().run()}
      >
        <Undo2 className="h-4 w-4" />
      </ToolbarButton>
      <ToolbarButton
        title="Znova"
        disabled={!editor.can().redo()}
        onClick={() => editor.chain().focus().redo().run()}
      >
        <Redo2 className="h-4 w-4" />
      </ToolbarButton>
    </div>
  )
}

const extensions = [
  Color.configure({ types: [TextStyle.name, ListItem.name] }),
  TextStyle,
  StarterKit.configure({
    bulletList: { keepMarks: true, keepAttributes: false },
    orderedList: { keepMarks: true, keepAttributes: false }
  })
]

export function Editor({
  content,
  onUpdate
}: {
  content: string
  onUpdate?: (content: string) => void
}) {
  return (
    <div className="overflow-hidden rounded-2xl border border-ink/[0.16] bg-white transition-colors focus-within:border-primary">
      <EditorProvider
        slotBefore={<MenuBar />}
        extensions={extensions}
        content={content}
        editorProps={{
          attributes: {
            class:
              'tiptap min-h-[200px] max-h-[480px] overflow-y-auto px-4 py-3 text-sm text-ink focus:outline-none'
          }
        }}
        onUpdate={({ editor }) => {
          onUpdate?.(editor.getHTML())
        }}
      />
    </div>
  )
}
